import { useEffect, useMemo, useState } from 'react';
import {
  BREAKDOWN_PANEL_CATEGORIES,
  ELEMENT_CATEGORIES,
  type CategoryCoverageDetail,
  type ElementCategory,
  type SceneItemView,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { DerivedRequirementsPanel } from '../production/DerivedRequirementsPanel.js';
import { SceneLocationRequirements, requirementsOfScene } from '../scenes/SceneLocationRequirements.js';
import type { SceneValidationRow } from '../../lib/api.js';
import { CategoryIcon } from './CategoryIcon.js';
import { useSceneBreakdown } from './context.js';
import { ItemRow } from './ItemRow.js';
import { KeyTakeawaysCard } from './KeyTakeawaysCard.js';
import { EMPTY_CATEGORY_TEXT, PARTIAL_CATEGORY_NOTE, itemsText, lastAttemptText } from './labels.js';

// The Location row's block is the scene review's own (`sw-locreq`).
import '../../styles/scenes.css';

/**
 * THE SCENE'S BREAKDOWN SHEET — every category, empty ones included.
 *
 * AN EMPTY CATEGORY SAYS WHAT WAS EXAMINED (rule 2). "None found" is a statement about the
 * screenplay and is made only when the AI reading examined every part of the scene for that
 * category (`CHECKED`); a category the reading skipped, failed on, read in part or never read
 * says exactly that instead. Every figure is the server's: the count beside a category is
 * `counts.byCategory`, never a count of the rows drawn.
 *
 * Categories with items come first, each in the sheet's own order, so the scene's work is at the
 * top and the statements about nothing sit below it — all seventeen always reachable.
 */
export function BreakdownTab() {
  const { detail, selectedItem, planOn, takeawaysOn, requirements, projectId } = useSceneBreakdown();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const items = useMemo(() => detail?.items ?? [], [detail]);
  const selected = selectedItem === null ? null : (items.find((i) => i.key === selectedItem) ?? null);

  // The category of the item in view opens, wherever the pick came from (the script, a takeaway).
  useEffect(() => {
    if (selected === null) return;
    setOpen((was) => (was.has(selected.category) ? was : new Set([...was, selected.category])));
  }, [selected]);

  if (detail === null) return null;

  const byCategory = new Map<ElementCategory, SceneItemView[]>();
  for (const item of items) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }
  const coverage = new Map<ElementCategory, CategoryCoverageDetail>(detail.categories.map((c) => [c.category, c]));
  const count = (category: ElementCategory) => detail.counts.byCategory[category] ?? 0;
  const ordered = [
    ...BREAKDOWN_PANEL_CATEGORIES.filter((c) => count(c) > 0),
    ...BREAKDOWN_PANEL_CATEGORIES.filter((c) => count(c) === 0),
  ];

  const toggle = (id: string) => {
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // What the Location row needs of the scene: its id, and whether a reading has looked at it.
  const locationScene = {
    id: detail.scene.id,
    analysis: detail.coverage === 'NOT_ANALYSED' ? 'NOT_ANALYSED' : detail.coverage === 'OUT_OF_DATE' ? 'OUT_OF_DATE' : 'CURRENT',
  } as SceneValidationRow;
  const locations = requirementsOfScene(requirements.data ?? [], detail.scene.id).inPlay;

  return (
    <div className="el-breakdown" data-testid="el-breakdown">
      <div className="el-section-head">
        <p className="el-section-title" data-testid="el-breakdown-title">
          Scene breakdown <span aria-hidden="true">·</span> <span data-testid="el-breakdown-items">{itemsText(detail.counts.items)}</span>
        </p>
        {detail.counts.awaiting > 0 ? (
          <p className="el-section-note" data-testid="el-breakdown-awaiting">
            {`${String(detail.counts.awaiting)} awaiting your decision`}
          </p>
        ) : null}
      </div>

      <ul className="el-cats" aria-label="Breakdown categories">
        <li className="el-cat" data-open={open.has('LOCATION') ? '1' : '0'} data-testid="el-cat-LOCATION">
          <button
            type="button"
            className="el-cat-row"
            aria-expanded={open.has('LOCATION')}
            data-testid="el-cat-toggle-LOCATION"
            onClick={() => {
              toggle('LOCATION');
            }}
          >
            <span className="el-cat-icon el-cat-icon-plain" aria-hidden="true">
              <Icon name="pin" size={17} />
            </span>
            <span className="el-cat-text">
              <span className="el-cat-label">Location</span>
              <span className="el-cat-preview">
                {locations.length > 0 ? locations.map((r) => r.title).join(', ') : 'Location requirements, read-only here'}
              </span>
            </span>
            {locations.length > 0 ? <span className="el-cat-count">{String(locations.length)}</span> : null}
            <span className="el-cat-chevron" aria-hidden="true">
              <Icon name="chevronDown" size={16} />
            </span>
          </button>
          {open.has('LOCATION') ? (
            <div className="el-cat-body el-cat-body-location">
              <SceneLocationRequirements projectId={projectId} scene={locationScene} read={requirements} />
            </div>
          ) : null}
        </li>

        {ordered.map((category) => (
          <CategoryRow
            key={category}
            category={category}
            items={byCategory.get(category) ?? []}
            count={count(category)}
            coverage={coverage.get(category) ?? null}
            open={open.has(category)}
            onToggle={() => {
              toggle(category);
            }}
            selectedItem={selectedItem}
          />
        ))}
      </ul>

      {takeawaysOn ? <KeyTakeawaysCard /> : null}

      {planOn ? (
        <div className="el-plan" data-testid="el-plan-derived">
          <DerivedRequirementsPanel sceneId={detail.scene.id} />
        </div>
      ) : null}

    </div>
  );
}

function CategoryRow({
  category,
  items,
  count,
  coverage,
  open,
  onToggle,
  selectedItem,
}: {
  category: ElementCategory;
  items: readonly SceneItemView[];
  /** The server's count for the category: distinct items that are not "not needed". */
  count: number;
  coverage: CategoryCoverageDetail | null;
  open: boolean;
  onToggle: () => void;
  selectedItem: string | null;
}) {
  const { add, busy, refusal } = useSceneBreakdown();
  const [showNotNeeded, setShowNotNeeded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const meta = ELEMENT_CATEGORIES[category];

  const live = items.filter((i) => i.state !== 'NOT_NEEDED');
  const notNeeded = items.filter((i) => i.state === 'NOT_NEEDED');
  const state = coverage?.coverage ?? 'NOT_ANALYSED';
  const empty = count === 0;
  // An empty category's line says what was examined; "None found" only when it was all examined.
  const emptyText = EMPTY_CATEGORY_TEXT[state];
  const partialNote = empty ? null : (PARTIAL_CATEGORY_NOTE[state] ?? null);
  const preview = live.slice(0, 3).map((i) => i.name);
  const more = live.length - preview.length;
  const holdsSelected = items.some((i) => i.key === selectedItem);

  return (
    <li
      className="el-cat"
      data-open={open ? '1' : '0'}
      data-empty={empty ? '1' : '0'}
      data-coverage={state}
      data-testid={`el-cat-${category}`}
    >
      <button
        type="button"
        className="el-cat-row"
        aria-expanded={open}
        data-testid={`el-cat-toggle-${category}`}
        data-holds-selected={holdsSelected ? '1' : '0'}
        onClick={onToggle}
      >
        <CategoryIcon category={category} />
        <span className="el-cat-text">
          <span className="el-cat-label">{meta.label}</span>
          {empty ? (
            <span className="el-cat-preview el-cat-coverage" data-state={state} data-testid={`el-cat-coverage-${category}`}>
              {emptyText}
            </span>
          ) : (
            <span className="el-cat-preview" data-testid={`el-cat-preview-${category}`}>
              {preview.join(', ')}
              {more > 0 ? ` +${String(more)}` : ''}
            </span>
          )}
        </span>
        {empty ? null : (
          <span className="el-cat-count" data-testid={`el-cat-count-${category}`} aria-label={`${String(count)} on the sheet or awaiting`}>
            {String(count)}
          </span>
        )}
        <span className="el-cat-chevron" aria-hidden="true">
          <Icon name="chevronDown" size={16} />
        </span>
      </button>

      {open ? (
        <div className="el-cat-body" data-testid={`el-cat-body-${category}`}>
          {partialNote === null ? null : (
            <p className="el-cat-note" data-testid={`el-cat-note-${category}`}>
              {partialNote}
            </p>
          )}
          {coverage?.lastAttempt == null ? null : (
            <p className="el-cat-note" data-testid={`el-cat-last-attempt-${category}`}>
              <Icon name="info" size={13} />
              {lastAttemptText(coverage.lastAttempt.status)}
            </p>
          )}

          {live.length === 0 ? null : (
            <ul className="el-items">
              {live.map((item) => (
                <ItemRow key={item.key} item={item} selected={item.key === selectedItem} />
              ))}
            </ul>
          )}

          {notNeeded.length === 0 ? null : (
            <>
              <button
                type="button"
                className="el-link el-not-needed-toggle"
                aria-expanded={showNotNeeded || notNeeded.some((i) => i.key === selectedItem)}
                data-testid={`el-cat-not-needed-${category}`}
                onClick={() => {
                  setShowNotNeeded((on) => !on);
                }}
              >
                {showNotNeeded ? `Hide ${String(notNeeded.length)} not needed` : `${String(notNeeded.length)} not needed · show`}
              </button>
              {showNotNeeded || notNeeded.some((i) => i.key === selectedItem) ? (
                <ul className="el-items el-items-muted">
                  {notNeeded.map((item) => (
                    <ItemRow key={item.key} item={item} selected={item.key === selectedItem} />
                  ))}
                </ul>
              ) : null}
            </>
          )}

          {adding ? (
            <form
              className="el-add"
              data-testid={`el-cat-add-form-${category}`}
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = name.trim();
                if (trimmed === '') return;
                add(category, trimmed);
                setAdding(false);
                setName('');
              }}
            >
              <input
                value={name}
                autoFocus
                maxLength={255}
                aria-label={`Add to ${meta.label}`}
                placeholder={`Name — ${meta.use.split(/[.:—]/)[0]?.trim() ?? meta.label}`}
                data-testid={`el-cat-add-input-${category}`}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setAdding(false);
                    setName('');
                  }
                }}
              />
              <button type="submit" className="ctl" disabled={name.trim() === '' || busy} data-testid={`el-cat-add-submit-${category}`}>
                Add
              </button>
              <button
                type="button"
                className="ctl"
                onClick={() => {
                  setAdding(false);
                  setName('');
                }}
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="el-link el-add-open"
              data-testid={`el-cat-add-${category}`}
              disabled={refusal !== null}
              title={refusal ?? undefined}
              onClick={() => {
                setAdding(true);
              }}
            >
              <Icon name="plus" size={13} />
              {`Add to ${meta.label}`}
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}
